// Smooths the page's jump whenever the promo banner region changes height.
//
// The banners sit at the very top of the body, so anything that adds or removes one shoves the
// whole page up or down under the customer's eyes. Two things do that, and both come through
// here (docs/promo-banners.md):
//
//   - changing spa, since banners are scoped per spa: none to one, one to two, two to none
//     (resources/js/shared/pageSwap.js)
//   - closing a banner (resources/js/promo-banner.js)
//
// ONLY when the height actually changes. Swapping one banner for another of the same height is
// the common case on a change of spa and wants no animation at all: the copy simply changes,
// the way the rest of the swapped page does. Animating that would draw the eye to a bar that
// has not moved.
//
// The technique is a FLIP-style first/last measurement around a synchronous DOM write. The
// caller hands over a function that does the replacing; this measures either side of it, and
// animates only if the two differ.

// Matched to the restore transition on swapped content (structure.scss:42, `opacity 400ms
// ease-out`), so the bar settling and the content brightening read as one movement rather than
// two effects of different lengths finishing at different moments.
const DURATION_MS = 400;

const EASING = 'ease-out';

// Inline rather than a shared helper: this is how the rest of the project asks the question
// (SearchBar.vue, ModeToggle.vue, PaymentPage.vue), and read at call time rather than at
// import so a customer changing the setting mid-session is respected without a reload.
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Run `replace` against `element`, animating its height if that changes it.
 *
 * @param {HTMLElement|null} element The wrapper whose height may change - #promo_banners.
 * @param {() => void} replace Swaps the contents. Called exactly once, synchronously.
 */
export function withHeightTransition(element, replace) {
    if (! element) {
        replace();

        return;
    }

    const before = element.getBoundingClientRect().height;

    replace();

    // Read back immediately: the write above was synchronous, so the new height is already
    // measurable and no frame has been painted in between.
    const after = element.getBoundingClientRect().height;

    // The case the whole module exists to skip. Nothing moved, so nothing is animated - a
    // banner replaced by another of the same height just changes its words.
    if (before === after) {
        return;
    }

    // Respecting the system setting matters more than usual here: this animation exists purely
    // to soften a movement, and someone who has asked for less motion wants the movement over
    // with, not eased.
    if (prefersReducedMotion()) {
        return;
    }

    // Animated rather than transitioned: the element's height is auto at both ends, and a CSS
    // transition cannot interpolate to or from auto. The Web Animations API takes the two
    // measured pixel values and leaves no inline style behind when it finishes.
    //
    // overflow hidden for the duration so a banner growing in does not spill out of the box
    // while the box is still smaller than it.
    const animation = element.animate(
        [
            { height: `${before}px`, overflow: 'hidden' },
            { height: `${after}px`, overflow: 'hidden' },
        ],
        {
            duration: DURATION_MS,
            easing: EASING,
        },
    );

    return animation;
}
